import React, { useCallback, useEffect, useState } from 'react';
import { AlertVariant, Button, Modal } from '@patternfly/react-core';
import { Folder, TFolder } from '../../../api/foundational-elements/Folder';
import DefaultPresentOptionsView, {
	DefaultPresentOptionsViewProps,
} from './DefaultPresentOptionsView';
import { Present, TPresent } from '../../../api/present/Present';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@zeroedin-tech/zi-common-ui/lib';
import { useMount } from 'react-use';
import BlankPresentOptionsView, { BlankPresentOptionsViewProps } from './BlankPresentOptionsView';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons';
import CopyPresentOptionsView, { CopyPresentOptionsViewProps } from './CopyPresentOptionsView';
import { useUser } from '../../user/ApplicationProvider';

export type ZiPresentOptionsModalProps = {
	isOpen: boolean;
	onClose: () => void;
};

export type PresentCreationOptions = 'default' | 'blank' | 'copy';

const ZiPresentOptionsModal = (props: ZiPresentOptionsModalProps) => {
	const [currentView, setCurrentView] = useState<PresentCreationOptions>('default');
	const [folders, setFolders] = useState<TFolder[]>([]);
	const [selectedFolder, setSelectedFolder] = useState<TFolder | undefined>();
	const [presentation, setPresentation] = useState<TPresent>(Present.Default());
	const navigate = useNavigate();
	const { addToast } = useToast();
	const pathSegments = window.location.pathname.split('/').filter(Boolean);
	const lastSegment = pathSegments[pathSegments.length - 1];
	const parentFolderId = +lastSegment;
	const currentUser = useUser();

	useMount(() => {
		Folder.GetAll({ type: 'presentations' })
			.then((foldersResponse) => {
				if (foldersResponse) {
					const actualFolders = foldersResponse.filter((x) => x.isFolder);

					setFolders([...actualFolders]);
					if (parentFolderId) {
						setSelectedFolder(actualFolders.find((f) => f.id === parentFolderId));
					}
				}
			})
			.catch(() => {
				addToast('Get all folders failed.', AlertVariant.danger);
			});
	});

	useEffect(() => {
		if (parentFolderId && folders) {
			setSelectedFolder(folders.find((f) => f.id === parentFolderId));
		} else {
			setSelectedFolder(undefined);
		}
	}, [window.location.pathname]);

	const handleNameChange = useCallback(
		(value: string, _event: React.FormEvent<HTMLInputElement>) => {
			setPresentation((presentation) => {
				return { ...presentation, name: value };
			});
		},
		[]
	);

	const handleCreate = () => {
		setCurrentView('blank');
	};

	const handleSave = () => {
		localStorage.setItem('currentSelectedPresentFolderId', selectedFolder?.id.toString() ?? '');
		localStorage.setItem('currentSelectedPresentId', presentation.id?.toString() ?? '0');
		localStorage.setItem('currentSelectedPresentName', presentation.name);

		if (currentView == 'copy') {
			navigate(`/present/create?isCopy=true`);
		} else {
			handlePresentaionSave();
		}
	};

	const handlePresentaionSave: () => void = () => {
		let newPresentationId = 0;

		const presentModel = Present.Default();
		presentModel.name = presentation.name;
		presentModel.owner = currentUser.id;
		presentModel.folder = selectedFolder?.id;

		Present.New(presentModel)
			.then((presentationResponse) => {
				newPresentationId = presentationResponse.id ?? 0;

				navigate(`/present/edit/${newPresentationId}`);
			})
			.catch((_) => {
				addToast('Presentation create failed.', AlertVariant.danger);
			});
	};

	const defaultViewProps: DefaultPresentOptionsViewProps = {
		onViewChange: setCurrentView,
		handleCreate: handleCreate,
	};

	const blankViewProps: BlankPresentOptionsViewProps = {
		onViewChange: setCurrentView,
		presentation: presentation,
		folders: folders,
		handleNameChange: handleNameChange,
		selectedFolder: selectedFolder,
		onSelectFolder: setSelectedFolder,
	};

	const copyPresentProps: CopyPresentOptionsViewProps = {
		onSelectPresent: setPresentation,
		selectedPresent: presentation,
	};

	const contentMap: Map<PresentCreationOptions, JSX.Element> = new Map<
		PresentCreationOptions,
		JSX.Element
	>([
		['default', <DefaultPresentOptionsView {...defaultViewProps} />],
		['blank', <BlankPresentOptionsView {...blankViewProps} />],
		// ['grid', <BlankPresentOptionsView {...blankViewProps} />],
		// ['flex', <BlankPresentOptionsView {...blankViewProps} />],
		['copy', <CopyPresentOptionsView {...copyPresentProps} />],
	]);

	const beforeClose = () => {
		setDefaultView();
		props.onClose();
	};

	const setDefaultView = (): void => {
		setCurrentView('default');
		setPresentation(Present.Default());
	};

	const backIcon = (
		<div className="back-btn">
			<Button
				variant="link"
				onClick={setDefaultView}
			>
				<FontAwesomeIcon icon={faArrowLeft} />
			</Button>
		</div>
	);

	return (
		<>
			<Modal
				data-testid="add-presentation-modal"
				isOpen={props.isOpen}
				onClose={beforeClose}
				variant="small"
				title="Add Presentation"
				actions={
					currentView !== 'default' && [
						<Button
							data-testid="create-button"
							key="create"
							variant="primary"
							onClick={handleSave}
							isDisabled={presentation.name === ''}
						>
							Create
						</Button>,
						<Button
							data-testid="cancel-button"
							key="cancel"
							variant="link"
							onClick={beforeClose}
						>
							Cancel
						</Button>,
					]
				}
			>
				{currentView !== 'default' ? backIcon : null}
				{contentMap.get(currentView)}
			</Modal>
		</>
	);
};

export default ZiPresentOptionsModal;
